// 422 validation error codes. Split out of auth.constants.ts (TO-591) to keep each
// constant file a cohesive, decomposable group under the module-size cap. Re-exported
// through the same `@today/types` barrel, so importers are unaffected.

/** Validation codes for 422 responses. */
export enum ValidationErrorCode {
	/** Default: the router's generic body-schema failure. */
	VALIDATION_ERROR = 'VALIDATION_ERROR',
	/** An offer's assigned content text has no localization in force in the requested locale (TO-429). */
	TEXT_NOT_AVAILABLE = 'TEXT_NOT_AVAILABLE',
	/** An inline prospect's email/phone does not parse (TO-122 offer-recipient-selection). */
	CUSTOMER_CONTACT_POINT_INVALID = 'CUSTOMER_CONTACT_POINT_INVALID',
	/** An inline prospect's creator does not sit in exactly one unit (TO-122 offer-recipient-selection). */
	CUSTOMER_ORG_UNIT_REQUIRED = 'CUSTOMER_ORG_UNIT_REQUIRED',
	/** An offer-texts request has 0 or more than 50 products, or repeats a product id (TO-429). */
	OFFER_TEXTS_REQUEST_INVALID = 'OFFER_TEXTS_REQUEST_INVALID',
	/** A send names a channel the tenant has not enabled for offers (TO-465 REQ-003). */
	OFFER_CHANNEL_NOT_ENABLED = 'OFFER_CHANNEL_NOT_ENABLED',
	/** A send names a channel the offer's customer has no primary, usable contact for (TO-465 REQ-004). */
	OFFER_RECIPIENT_UNREACHABLE = 'OFFER_RECIPIENT_UNREACHABLE',
	/** A send of an offer that has no products and/or no call to action (TO-125 REQ-005). */
	OFFER_INCOMPLETE = 'OFFER_INCOMPLETE',
	/** A send names a channel the customer has no MARKETING consent for (TO-125 REQ-006). */
	OFFER_CONSENT_MISSING = 'OFFER_CONSENT_MISSING',
	/** A send of an offer whose `expiresAt` is at or before the send instant (TO-125 REQ-007). */
	OFFER_EXPIRED_BEFORE_SEND = 'OFFER_EXPIRED_BEFORE_SEND',
	/** A missing or invalid field on the `/admin/tenants` surface (TO-306 REQ-004). */
	VALIDATION_FAILED = 'VALIDATION_FAILED',
	/**
	 * An SSO attribute mapping names `superadmin` or another tenant's group (TO-308 REQ-005),
	 * or an attribute-mapping role rule targets `ORG_ADMIN`/`PLATFORM_ADMIN`/`SUPER_ADMIN` (TO-377 REQ-004).
	 */
	IDP_MAPPING_REJECTED = 'IDP_MAPPING_REJECTED',
	/** An attribute-mapping transform lacks its parameter, carries another type's parameter, or has an unsafe pattern (TO-377 REQ-003). */
	IDP_MAPPING_TRANSFORM_INVALID = 'IDP_MAPPING_TRANSFORM_INVALID',
	/** A SCIM mapping rule's `idpAttribute` is not a SCIM attribute path (TO-379 scim-connection REQ-006). */
	SCIM_PATH_INVALID = 'SCIM_PATH_INVALID',
	/** An attribute mapping leaves `EMAIL` unmapped (TO-377 REQ-003). */
	IDP_MAPPING_REQUIRED_FIELD_UNMAPPED = 'IDP_MAPPING_REQUIRED_FIELD_UNMAPPED',
	/** Cognito rejected the submitted SAML metadata (TO-308). */
	IDP_METADATA_INVALID = 'IDP_METADATA_INVALID',
	/** Cognito rejected the chosen first-login password under the pool policy (TO-309). */
	PASSWORD_POLICY_VIOLATION = 'PASSWORD_POLICY_VIOLATION',
	/** A JIT `defaultRole` outside `JIT_ASSIGNABLE_ROLES` (`ORG_ADMIN` and above) (TO-335 REQ-003, TO-410). */
	JIT_ROLE_REJECTED = 'JIT_ROLE_REJECTED',
	/** Webhook endpoint registration named a provider outside the listed set (TO-323 REQ-003). */
	WEBHOOK_PROVIDER_UNKNOWN = 'WEBHOOK_PROVIDER_UNKNOWN',
	/** A webhook ledger list filter, page size or instant could not be parsed (TO-323 REQ-011). */
	WEBHOOK_QUERY_INVALID = 'WEBHOOK_QUERY_INVALID',
	/** `fileName` is outside 1–255 code points after trimming (TO-324 REQ-001/REQ-005). */
	FILE_NAME_INVALID = 'FILE_NAME_INVALID',
	/** `contentType` is outside the accepted media-type set (TO-324 REQ-001). */
	FILE_CONTENT_TYPE_INVALID = 'FILE_CONTENT_TYPE_INVALID',
	/** `sizeBytes` is not an integer in 1–5368709120 (TO-324 REQ-001). */
	FILE_SIZE_INVALID = 'FILE_SIZE_INVALID',
	/** Exactly one of `entityType`/`entityId` is present, or either exceeds 64 characters (TO-324 REQ-001). */
	FILE_ENTITY_REF_INVALID = 'FILE_ENTITY_REF_INVALID',
	/** `partCount` is not an integer in 1–10000 (TO-324 REQ-009). */
	FILE_MULTIPART_PART_COUNT_INVALID = 'FILE_MULTIPART_PART_COUNT_INVALID',
	/** `domain` is not a domain the route accepts (TO-443 file-storage BR-2). */
	FILE_DOMAIN_INVALID = 'FILE_DOMAIN_INVALID',
	/** The stored bytes do not match the declared size, tenant or media type; the file is quarantined (TO-443). */
	FILE_CONTENT_MISMATCH = 'FILE_CONTENT_MISMATCH',
	/** A branding image's source exceeds 25000000 pixels (TO-443 public-branding-assets BR-4). */
	FILE_IMAGE_PIXELS_EXCEEDED = 'FILE_IMAGE_PIXELS_EXCEEDED',
	/** A branding image's bytes cannot be decoded (TO-443 public-branding-assets BR-4). */
	FILE_IMAGE_UNREADABLE = 'FILE_IMAGE_UNREADABLE',
	/** A product CTA is half-set or its target does not match its type (TO-415). */
	PRODUCT_CTA_INVALID = 'PRODUCT_CTA_INVALID',
	/** A product's `categoryId` names no live category of the caller's tenant (TO-415). */
	PRODUCT_CATEGORY_INVALID = 'PRODUCT_CATEGORY_INVALID',
	/** The locale is not one of the configured tenant's enabled locales (TO-415). */
	PRODUCT_LOCALE_NOT_ENABLED = 'PRODUCT_LOCALE_NOT_ENABLED',
	/** Publishing is blocked: a required locale has no translation (TO-415). */
	PRODUCT_TRANSLATIONS_MISSING = 'PRODUCT_TRANSLATIONS_MISSING',
	/** The brand id is not a `published` brand of the runtime catalogue — deprecated, draft or unknown (TO-380). */
	BRAND_NOT_SELECTABLE = 'BRAND_NOT_SELECTABLE',
	/** A tenant locale-settings body breaks a subset/count/format rule (TO-415). */
	LOCALE_SETTINGS_INVALID = 'LOCALE_SETTINGS_INVALID',
	/** An onboarding PATCH asked to skip a required step (TO-190 REQ-003). */
	ONBOARDING_STEP_NOT_SKIPPABLE = 'ONBOARDING_STEP_NOT_SKIPPABLE',
	/** A provider `PATCH` changed its `code` or `protocol` (TO-410). */
	IDP_FIELD_IMMUTABLE = 'IDP_FIELD_IMMUTABLE',
	/** A certificate rotation was asked of an OIDC provider; only SAML has one (TO-410, narrowed by TO-378). */
	IDP_PROTOCOL_NOT_SUPPORTED = 'IDP_PROTOCOL_NOT_SUPPORTED',
	/** `defaultProviderId` is not a usable provider of the tenant (TO-410). */
	IDP_DEFAULT_INVALID = 'IDP_DEFAULT_INVALID',
	/** The write would leave the tenant with no usable login path (TO-410 design D6). */
	AUTH_CONFIG_LOCKOUT = 'AUTH_CONFIG_LOCKOUT',
	/** A file-exchange configuration breaks one or more field rules; `details.fields` lists them (TO-391). */
	FILE_CONFIG_INVALID = 'FILE_CONFIG_INVALID',
	/** The users file was saved enabled while provisioning is `JIT` or `JIT_WITH_ALLOWLIST` (TO-391). */
	USERS_FILE_REQUIRES_NON_JIT_PROVISIONING = 'USERS_FILE_REQUIRES_NON_JIT_PROVISIONING',
	/** An import-definition contract breaks one or more field rules; `details.fields` lists them (TO-414). */
	IMPORT_DEFINITION_INVALID = 'IMPORT_DEFINITION_INVALID',
	/** A file configuration references a missing or non-ACTIVE version; `details.reason` (TO-414). */
	FILE_CONFIG_IMPORT_DEFINITION_UNUSABLE = 'FILE_CONFIG_IMPORT_DEFINITION_UNUSABLE',
	/** An SFTP connector body breaks one or more field rules; `details.fields` (TO-393). */
	CONNECTOR_INVALID = 'CONNECTOR_INVALID',
	/** An upload start body breaks one or more field rules; `details.fields` lists them (TO-392 BR-1). */
	IMPORT_UPLOAD_INVALID = 'IMPORT_UPLOAD_INVALID',
	/** `GET /events/integrity` got a `fromSeq` or `limit` outside its integer range (TO-230). */
	AUDIT_CHAIN_QUERY_INVALID = 'AUDIT_CHAIN_QUERY_INVALID',
	/** The logical file is disabled or names no import definition (TO-392 BR-2). */
	IMPORT_FILE_NOT_ENABLED = 'IMPORT_FILE_NOT_ENABLED',
	/** The transport does not match the credential or is not configured; `details.reason` (TO-392 BR-3). */
	IMPORT_TRANSPORT_NOT_ALLOWED = 'IMPORT_TRANSPORT_NOT_ALLOWED',
	/** The filename does not match the method's pattern (TO-392 BR-4). */
	IMPORT_FILENAME_NOT_ALLOWED = 'IMPORT_FILENAME_NOT_ALLOWED',
	/** The declared size exceeds the method's limit; `details.maxFileSizeBytes` (TO-392 BR-5). */
	IMPORT_FILE_SIZE_EXCEEDED = 'IMPORT_FILE_SIZE_EXCEEDED',
	/** The file's import definition version is not ACTIVE; `details.reason` (TO-392 BR-7). */
	IMPORT_DEFINITION_UNUSABLE = 'IMPORT_DEFINITION_UNUSABLE',
	/** An outbound `targetUrl` breaks the https/port/host rules (TO-381 BR-1). */
	WEBHOOK_TARGET_URL_INVALID = 'WEBHOOK_TARGET_URL_INVALID',
	/** An outbound subscription or payload-shape path names an event type outside the catalog (TO-381 BR-2, TO-472). */
	WEBHOOK_EVENT_TYPE_UNKNOWN = 'WEBHOOK_EVENT_TYPE_UNKNOWN',
	/** An outbound subscription body is empty, has duplicates or unknown properties (TO-381). */
	WEBHOOK_SUBSCRIPTION_INVALID = 'WEBHOOK_SUBSCRIPTION_INVALID',
	/** A payload-shape field is not an available field of the event type (TO-381). */
	WEBHOOK_PAYLOAD_FIELD_UNKNOWN = 'WEBHOOK_PAYLOAD_FIELD_UNKNOWN',
	/** A payload-shape body is malformed: missing/duplicate/non-string fields, unknown keys (TO-381). */
	WEBHOOK_PAYLOAD_SHAPE_INVALID = 'WEBHOOK_PAYLOAD_SHAPE_INVALID',
	/** An email settings body breaks a field rule; `details.field` names it (TO-382). */
	NOTIFICATION_BINDING_INVALID = 'NOTIFICATION_BINDING_INVALID',
	/** Domain verification needs a platform-credential binding whose from address is on a non-platform domain (TO-382). */
	SENDER_DOMAIN_NOT_APPLICABLE = 'SENDER_DOMAIN_NOT_APPLICABLE',
	/** A notification toggle item or list breaks a rule; `details.index` names the item when there is one (TO-382). */
	NOTIFICATION_TOGGLE_INVALID = 'NOTIFICATION_TOGGLE_INVALID',
	/** A required notification toggle was set to disabled (TO-382). */
	NOTIFICATION_TOGGLE_REQUIRED = 'NOTIFICATION_TOGGLE_REQUIRED',
	/** An SMS settings or test-SMS body breaks a field rule; `details.field` names it (TO-383). */
	SMS_SETTINGS_INVALID = 'SMS_SETTINGS_INVALID',
	/** A PDF watermark settings body breaks a field rule; `details.field` names it, `details.token` a refused token (TO-495). */
	PDF_WATERMARK_INVALID = 'PDF_WATERMARK_INVALID',
	/** A test SMS on a non-prod stage named a phone outside the dev allowlist (TO-383). */
	SMS_TEST_RECIPIENT_NOT_ALLOWED = 'SMS_TEST_RECIPIENT_NOT_ALLOWED',
	/** A test SMS named a destination country the tenant does not allow (TO-383). */
	SMS_COUNTRY_NOT_ALLOWED = 'SMS_COUNTRY_NOT_ALLOWED',
	/** A test SMS would take the tenant's month SMS spend over its cap (TO-383). */
	SMS_SPEND_CAP_REACHED = 'SMS_SPEND_CAP_REACHED',
	/** A test SMS: the provider rejected the tenant's credentials (TO-383). */
	SMS_PROVIDER_AUTH_FAILED = 'SMS_PROVIDER_AUTH_FAILED',
	/** A test SMS: the provider refused the message; `details.failureCode` says why (TO-383). */
	SMS_PROVIDER_REJECTED = 'SMS_PROVIDER_REJECTED',
	/** A unit-scoped caller created or invited a user without naming the org unit to place them in (TO-435 REQ-006). */
	USER_ORG_UNIT_REQUIRED = 'USER_ORG_UNIT_REQUIRED',
	/** A new user's `orgUnitId` names no assignable unit of the target tenant (TO-435 REQ-006/REQ-007). */
	USER_ORG_UNIT_NOT_ASSIGNABLE = 'USER_ORG_UNIT_NOT_ASSIGNABLE',
	/** A notification template breaks a field bound; `details.fields` lists them in REQ-001 order (TO-140). */
	NOTIFICATION_TEMPLATE_INVALID = 'NOTIFICATION_TEMPLATE_INVALID',
	/** A template subject/body does not parse as message Liquid; `details.field` names it (TO-140). */
	NOTIFICATION_TEMPLATE_SYNTAX_INVALID = 'NOTIFICATION_TEMPLATE_SYNTAX_INVALID',
	/** A template preview failed at render time (helper error or render bound) (TO-140). */
	NOTIFICATION_TEMPLATE_RENDER_FAILED = 'NOTIFICATION_TEMPLATE_RENDER_FAILED',
	/** A template would be both inactive and the default of its channel/locale (TO-140). */
	NOTIFICATION_TEMPLATE_DEFAULT_INACTIVE = 'NOTIFICATION_TEMPLATE_DEFAULT_INACTIVE',
	/** An offer links (or is sent with) a template that is inactive, deleted, unknown or another tenant's (TO-140). */
	NOTIFICATION_TEMPLATE_UNAVAILABLE = 'NOTIFICATION_TEMPLATE_UNAVAILABLE',
	/** An offer's custom notification body is blank, too long or unparsable; `details.reason` (TO-140). */
	OFFER_NOTIFICATION_BODY_INVALID = 'OFFER_NOTIFICATION_BODY_INVALID',
	/** One request set both a notification template and a custom body (TO-140). */
	OFFER_NOTIFICATION_MESSAGE_CONFLICT = 'OFFER_NOTIFICATION_MESSAGE_CONFLICT',
}
